import React, { useState } from "react";
import { ArrowButton } from "../components/buttons/arrow-button.tsx";
import { cn } from "../utils/cn.ts";
import { FontStyles } from "../utils/font-styles.ts";

const storeBtn = "flex items-center gap-2 h-[40px] px-3 rounded-md no-underline flex-shrink-0";

const AppStoreButton: React.FC = () => (
  <a href="https://apps.apple.com/us/app/espc/id6754396148" className={`${storeBtn} bg-black border border-[#a6a6a6]`}>
    <img
      src="/static/icons/apple-white.svg"
      alt=""
      aria-hidden="true"
      width={20}
      height={24}
      style={{ width: "20px", height: "24px", flexShrink: 0 }}
    />
    <div>
      <div style={{ fontSize: "9px", color: "white", lineHeight: 1.2, fontWeight: 500 }}>Download on the</div>
      <div style={{ fontSize: "17px", color: "white", lineHeight: 1, fontWeight: 600, letterSpacing: "-0.5px" }}>
        App Store
      </div>
    </div>
  </a>
);

const PlayStoreButton: React.FC = () => (
  <a
    href="https://play.google.com/store/apps/details?id=com.espc.public"
    className={`${storeBtn} bg-neutral-white border border-black`}
  >
    <img
      src="/static/icons/playstore.svg"
      alt=""
      aria-hidden="true"
      width={21}
      height={24}
      style={{ width: "21px", height: "24px", flexShrink: 0 }}
    />
    <div>
      <div
        style={{
          fontSize: "9px",
          color: "#000",
          lineHeight: 1.2,
          fontWeight: 500,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        GET IT ON
      </div>
      <div style={{ fontSize: "17px", color: "#000", lineHeight: 1, fontWeight: 600, letterSpacing: "-0.5px" }}>
        Google Play
      </div>
    </div>
  </a>
);

const FEATURE_SLIDES = [
  {
    src: "/static/images/app-screenshots/property-search.png",
    alt: "Property Search Screenshot",
    title: "Property Search",
    body: "Be first to know when new properties hit the market. Our comprehensive search gives you instant access to the latest homes for sale across Scotland, updated in real-time.",
  },
  {
    src: "/static/images/app-screenshots/map-search.png",
    alt: "Map Search Screenshot",
    title: "Map Search",
    body: "Explore properties visually with our interactive map. Zoom into any area of Scotland to see exactly what's available and get a true sense of location before you book a viewing.",
  },
  {
    src: "/static/images/app-screenshots/filter.png",
    alt: "Filter Screenshot",
    title: "Filter & Sort",
    body: "Narrow your search with powerful filters. Refine by price, number of bedrooms, property type, and more to quickly find exactly what you're looking for.",
  },
  {
    src: "/static/images/app-screenshots/property-details.png",
    alt: "Property Details Screenshot",
    title: "Property Details",
    body: "Get all the information you need at a glance. View high-quality photos, floor plans, key property details, and nearby amenities for every listing.",
  },
  {
    src: "/static/images/app-screenshots/saved-properties.png",
    alt: "Saved Properties Screenshot",
    title: "Saved Properties",
    body: "Save your favourites and never lose track of a home you love. Keep all the properties you're interested in organised in one place and revisit them at any time.",
  },
  {
    src: "/static/images/app-screenshots/home-reports.png",
    alt: "Home Reports Screenshot",
    title: "Home Reports",
    body: "Access official Home Reports instantly and for free. Read independent condition surveys and energy performance ratings for any property before you decide to view.",
  },
  {
    src: "/static/images/app-screenshots/calculators.png",
    alt: "Calculators Screenshot",
    title: "Calculators",
    body: "Plan your finances with confidence using our built-in mortgage calculators. Quickly estimate your monthly repayments and understand your buying power before you start searching.",
  },
  {
    src: "/static/images/app-screenshots/sold-prices.png",
    alt: "Sold Prices Screenshot",
    title: "Sold Prices",
    body: "Make informed decisions with access to historical sold prices. Research what properties have actually sold for in any area across Scotland to better understand true market value.",
  },
];

export const AppLandingPage: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const count = FEATURE_SLIDES.length;

  const prev = () => setActiveIndex((i) => (i - 1 + count) % count);
  const next = () => setActiveIndex((i) => (i + 1) % count);

  return (
    <div style={{ fontFamily: "'Catamaran', sans-serif" }}>
      {/* ============================================================ */}
      {/* SECTION 1 · HERO                                             */}
      {/* ============================================================ */}
      <section
        className="bg-north-sea relative overflow-hidden
                    px-3.5 py-10 xss:py-15
                    xs:pb-20 xss:px-7.5 sm:px-0 xs:pt-[110px]"
      >
        <div className="absolute top-0 left-0 right-0  z-10">
          <div className="mx-auto w-full sm:max-w-[1080px] px-3.5 xss:px-7.5 sm:px-10 flex justify-end xs:justify-start">
            <a href={"/"}>
              <div className="inline-block bg-neutral-white rounded-b-2xl sm:pt-8 sm:px-4 sm:pb-4 xs:px-4 xs:pt-6 pt-5.5 px-3 pb-3">
                <img src="/static/logos/espc-blue-logo.png" alt="ESPC" className="block w-15   xs:w-20 h-auto" />
              </div>
            </a>
          </div>
        </div>

        <div className="mx-auto w-full max-w-none px-0 sm:max-w-[1080px] sm:px-10">
          <div className="block sm:flex sm:gap-15 sm:items-center">
            {/* Text copy */}
            <div className="flex-1 min-w-0 flex flex-col gap-7.5 xs:pr-[160px] sm:pr-0">
              <div className="flex flex-col gap-3">
                {/* Badge pill */}
                <div className="inline-flex">
                  <span
                    className="text-neutral-white font-medium px-4 py-3 rounded-full leading-none text-body-xs sm:text-body-s"
                    style={{ background: "rgba(255,255,255,0.1)" }}
                  >
                    Now available on iOS and Android
                  </span>
                </div>

                {/* Headline */}
                <h1
                  className={cn(FontStyles.h1, "text-neutral-white font-semibold leading-snug ")}
                  style={{ letterSpacing: "0.05px" }}
                >
                  Introducing the new ESPC mobile app
                </h1>

                {/* Phone screenshot – small mobile only (below xss), in-flow between headline and body */}
                <div className="flex items-center justify-center xs:hidden py-2">
                  <img
                    src="/static/images/app-screenshots/main.png"
                    alt="ESPC App Screenshot"
                    className="w-[160px] h-auto"
                  />
                </div>

                {/* Body */}
                <div className={cn(FontStyles["body-xl"], "text-neutral-white")}>
                  <p className="mb-3">
                    When it comes to property search in Scotland, <strong className="font-medium">ESPC</strong> is where
                    buyers and sellers start. Our market research shows that{" "}
                    <a href="https://espc.com" className="text-[#00aeef] underline font-medium">
                      ESPC
                    </a>{" "}
                    is the first property portal of choice for buyers and sellers.
                    <br />
                    <br />
                    Our new mobile app makes it easier than ever to search, save, and secure your dream home. Whether
                    you&apos;re browsing on your commute or researching from your sofa, everything you need to find your
                    perfect property is now at your fingertips.
                  </p>
                </div>
              </div>

              {/* Store buttons */}
              <div className="flex gap-3 items-center">
                <AppStoreButton />
                <PlayStoreButton />
              </div>
            </div>

            {/* Phone screenshot – xs+ absolutely positioned (bleeds right), sm+ in-flow right column */}
            <div
              className="hidden xs:flex items-center justify-center flex-shrink-0
                            xs:absolute xs:top-1/2 xs:-translate-y-1/2 xs:right-[-60px]
                            sm:relative sm:top-auto sm:translate-y-0 sm:right-auto"
            >
              <img
                src="/static/images/app-screenshots/main.png"
                alt="ESPC App"
                className="w-[227px] h-auto sm:w-[220px] mw:w-auto mw:h-[536px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 2 · FEATURES CAROUSEL                                */}
      {/* ============================================================ */}
      <section className="bg-white py-10 xs:py-20">
        {/* Header – constrained */}
        <div
          className="mx-auto w-full px-3.5 xss:px-7.5 sm:px-10 mb-10 xs:mb-15 text-center"
          style={{ maxWidth: "1080px" }}
        >
          <h2 className={cn(FontStyles.h2, "text-slate font-semibold m-0 mb-3")}>Features</h2>
          <p className={cn(FontStyles["body-xl"], "text-light-slate")}>
            Everything you need to find, explore, and secure your perfect property, all in one powerful app.
          </p>
        </div>

        {/* Fade carousel */}
        <div className="relative">
          {/* Slides stacked in the same grid cell */}
          <div style={{ display: "grid" }}>
            {FEATURE_SLIDES.map((slide, i) => (
              <div
                key={slide.alt}
                style={{
                  gridArea: "1 / 1",
                  alignSelf: "start",
                  opacity: i === activeIndex ? 1 : 0,
                  transition: "opacity 0.5s ease",
                  pointerEvents: i === activeIndex ? "auto" : "none",
                }}
              >
                <div className="mx-auto w-full px-3.5 xss:px-7.5 sm:px-10" style={{ maxWidth: "1080px" }}>
                  <div className="flex flex-col items-center gap-5 sm:flex-row sm:gap-7.5 sm:gap-15 xs:px-20">
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      className="w-[140px] h-[270px] xs:w-[160px] xs:h-[309px] sm:w-[220px] sm:h-auto object-cover flex-shrink-0 mw:w-auto mw:h-[495px]"
                      style={{
                        transform: i === activeIndex ? "scale(1)" : "scale(0.97)",
                        transformOrigin: "center center",
                        transition: "transform 0.5s ease",
                      }}
                    />
                    <div className="flex flex-col gap-3 flex-1">
                      <h3
                        className={cn(
                          FontStyles.h3,
                          "text-north-sea font-semibold leading-none m-0 text-center sm:text-left",
                        )}
                      >
                        {slide.title}
                      </h3>
                      <p className={cn(FontStyles["body-l"], "text-light-slate m-0 text-center sm:text-left")}>
                        {slide.body}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 md:mt-0 mt-4" role="tablist" aria-label="Feature slides">
            {FEATURE_SLIDES.map((slide, i) => (
              <button
                key={slide.alt}
                role="tab"
                aria-selected={i === activeIndex}
                aria-label={slide.title}
                onClick={() => setActiveIndex(i)}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background: "#0677b9",
                  opacity: i === activeIndex ? 1 : 0.35,
                  transition: "opacity 0.3s ease",
                }}
              />
            ))}
          </div>

          {/* Arrows – absolutely positioned but visually constrained to the content column */}
          <div
            className="absolute top-[48%] -translate-y-1/2 z-10 left-0 right-0 pointer-events-none"
            aria-hidden="true"
          >
            <div
              className="mx-auto w-full px-3.5 xss:px-7.5 sm:px-10 flex justify-between"
              style={{ maxWidth: "1080px" }}
            >
              <div className="pointer-events-auto">
                <ArrowButton direction="prev" onClick={prev} colourStyle="dark" ariaLabel="Previous slide" />
              </div>
              <div className="pointer-events-auto">
                <ArrowButton direction="next" onClick={next} colourStyle="dark" ariaLabel="Next slide" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3 · WHY CHOOSE ESPC (STATS)                         */}
      {/* ============================================================ */}
      <section className="bg-cloud py-10 xs:py-15">
        <div
          className="mx-auto w-full px-3.5 xss:px-7.5 gap-10 sm:px-10 sm:gap-15 flex flex-col items-center"
          style={{ maxWidth: "1080px" }}
        >
          <div className="text-center flex flex-col gap-3">
            <h2 className={cn(FontStyles.h4, "text-slate font-semibold leading-none m-0")}>Why choose ESPC?</h2>
            <p className={cn(FontStyles["body-md"], "text-light-slate")}>
              Please note that the data below represents Jan-Dec 2025
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-10 sm:flex-nowrap sm:justify-start items-start w-full">
            {[
              { value: "550k", label: "Website users\nper month" },
              { value: "11,700+", label: "Properties sold\nlast year" },
              { value: "130", label: "Solicitor estate agents to choose from" },
              { value: "3.9x", label: "More leads than other websites" },
            ].map((stat) => (
              <div
                key={stat.value}
                className="flex-none w-[260px] xss:w-[172px] sm:flex-1 sm:w-auto flex flex-col gap-2.5 items-center text-center"
              >
                <span className={cn(FontStyles.h2, "text-loch font-semibold")}>{stat.value}</span>
                <p className={cn(FontStyles["body-xl"], "text-light-slate")}>
                  {stat.label.split("\n").map((line, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4 · CTA – START YOUR SEARCH                         */}
      {/* ============================================================ */}
      <section className="bg-north-sea py-10 xs:py-15 overflow-hidden">
        <div
          className="mx-auto w-full px-3.5 xss:px-7.5 sm:px-10 flex flex-col gap-7.5 items-center text-center"
          style={{ maxWidth: "700px" }}
        >
          <div className="flex flex-col gap-3">
            <h2 className={cn(FontStyles.h2, "text-neutral-white font-semibold leading-snug m-0")}>
              Start your property search today
            </h2>
            <p className={cn(FontStyles["body-l"], "text-neutral-white")}>
              Join thousands of home seekers who trust{" "}
              <a href="/" className="text-[#00aeef] underline">
                ESPC
              </a>{" "}
              to find their perfect property.
            </p>
          </div>

          {/* Store buttons */}
          <div className="flex gap-3 items-center">
            <AppStoreButton />
            <PlayStoreButton />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 · FOOTER COPYRIGHT                                 */}
      {/* ============================================================ */}
      <section className="bg-legacy-primary py-2.5">
        <div className="flex items-center justify-center px-3.5 text-center">
          <p className={cn(FontStyles["body-s"], "text-neutral-white m-0 whitespace-nowrap")}>
            © {new Date().getFullYear()}{" "}
            <a href="/" className="text-[#00aeef] underline">
              ESPC (UK) Ltd
            </a>
            . All rights reserved.
          </p>
        </div>
      </section>
    </div>
  );
};
